/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { VignetteType } from '../algorithm/vignettes_types';
import {
  JourneyType,
  PrimaryJourneyType,
  UserData,
  UserState,
  allJourneys,
  journeyNameToIndex,
  primaryJourneys,
} from './types';

type VignettesJsonFileType = {
  journeys: {
    [journey: string]: {
      name: string;
      vignettes: string[];
    };
  };
  vignettes: {
    [vignette: string]: VignetteType;
  };
};

type JourneyVignetteInfo = {
  journey: JourneyType;
  journeyN: number;
  vignette: string;
  howManyToShowTogether: number;
};

/** Create the complete list of videos to watch, interleaving the journeys.
 *
 *  Set @p filterSeenFirst to true if the user hasn't watched for a while,
 *  so you want the highest priority video first, and false if they've watched recently
 *  and so you just want the next video to watch.
 */
export function calculatePathway(
  json: VignettesJsonFileType,
  data: UserData,
  showOtherJourneysToo = true,
  currentDate: Date
): { vignettes: string[]; debug?: string } {
  let { v, debug } = stateMachineGetNextVignette(json, data, showOtherJourneysToo, true /* debug */);
  debugVignetteReason = debug;
  if (!v) {
    return { vignettes: [] }; // No more videos
  }
  const vignettes = [v];
  v = stateMachineGetNextVignette(
    json,
    updateStateUserWatchedVideo(json, data, v, true, currentDate),
    showOtherJourneysToo,
    false /* debug */
  ).v;
  if (v) {
    vignettes.push(v);
  }
  return { vignettes, debug };
}

function journeyToJourneyN(journey: JourneyType | undefined) {
  if (!journey) return 0;
  return journeyNameToIndex.get(journey) || 0;
}

const GAP_BETWEEN_QUIZES = 1; // Every other quiz
const MAX_CUT_OFF_DAYS = 15;
const DEMO_PRETEND_ALL_HAVE_QUIZES = true;
const OUTPUT_CSV = true;
const MINIMAL_OUTPUT = true;

/** Remove seen journeys older than MAX_CUTO_OFF_DAYS */
function truncateLastSeenJourneys(state_: UserState | undefined, currentDate: Date): UserState | undefined {
  if (!state_) return undefined;
  const state = { ...state_ };
  // Ignore what was watched more than MAX_CUT_OFF_DAYS ago
  const cutOffIndex = state.lastJourneysWatched.findIndex((x, i) => {
    const num_days = (currentDate.getTime() - state!.lastJourneysWatchedDate[i]!.getTime()) / (24 * 60 * 60 * 1000);
    if (currentDate.getTime() - num_days > MAX_CUT_OFF_DAYS) {
      return true;
    }
    return false;
  });
  if (cutOffIndex >= 0) {
    state.lastJourneysWatched.length = cutOffIndex;
    state.lastJourneysWatchedDate.length = cutOffIndex;
  }
  if (state.lastJourneysWatched.length === 0) {
    // Reset the internal state
    resetInternalState(state);
  }
  return state;
}

function resetInternalState(state: UserState) {
  state.watchedVideosSinceLastQuiz = 0;
}

function validateUserData(user: UserData): UserData {
  // Make sure that there is nothing in both flaggedJourneys and nonFlaggedJourneys
  if (user.flaggedJourneys.some((j) => user.nonFlaggedJourneys.includes(j as any))) {
    throw new Error('Journey is in both flaggedJourneys and nonFlaggedJourneys');
  }
  // Make sure everything in flaggedJourneys is in allJourneys
  if (user.flaggedJourneys.some((j) => !allJourneys.includes(j))) {
    throw new Error('Journey is in flaggedJourneys but not in allJourneys');
  }
  // Make sure everything in nonFlaggedJourneys is in primaryJourneys
  if (user.nonFlaggedJourneys.some((j) => !primaryJourneys.includes(j))) {
    throw new Error('Journey is in nonFlaggedJourneys but not in primaryJourneys');
  }
  // Make sure the order in flaggedJourneys is the same as in allJourneys. i.e. If allJourneys is [A, B, C] then flaggedJourneys can be [A, C] but not [C, A]
  const flaggedJourneysSet = new Set(user.flaggedJourneys);
  const allJourneysFiltered = allJourneys.filter((j) => flaggedJourneysSet.has(j));
  if (JSON.stringify(allJourneysFiltered) !== JSON.stringify(user.flaggedJourneys)) {
    // throw new Error('flaggedJourneys is not in the right order (must be same as allJourneys)');
    // just fix it
    user = { ...user, flaggedJourneys: allJourneysFiltered };
  }
  // Make sure the order in nonFlaggedJourneys is the same as in primaryJourneys. i.e. If primaryJourneys is [A, B, C] then nonFlaggedJourneys can be [A, C] but not [C, A]
  const nonFlaggedJourneysSet = new Set(user.nonFlaggedJourneys);
  const primaryJourneysFiltered = primaryJourneys.filter((j) => nonFlaggedJourneysSet.has(j));
  if (JSON.stringify(primaryJourneysFiltered) !== JSON.stringify(user.nonFlaggedJourneys)) {
    // throw new Error('nonFlaggedJourneys is not in the right order (must be same as primaryJourneys)');
    // just fix it
    user = { ...user, nonFlaggedJourneys: primaryJourneysFiltered };
  }
  return user;
}

export function calculateNextPathwayStep(
  json: VignettesJsonFileType,
  user: UserData,
  currentDate: Date,
  showQuizOnFirstVideo: boolean = false
): {
  nextVignetteID: string | undefined;
  nextVignette: VignetteType | undefined;
  nextNextVignette: VignetteType | undefined;
  shouldShowQuiz: boolean;
  state: UserState | undefined;
} {
  user = validateUserData(user);
  /// Setup
  // 1. Truncate lastJourneysWatchedDate to those younger than MAX_CUT_OFF_DAYS and truncate lastJourneysWatched to match length
  const state = truncateLastSeenJourneys(user.currentState, currentDate);
  const { flaggedJourneys, nonFlaggedJourneys, seenVignettes } = user;
  const { vignettes: nextVignettes } = calculatePathway(
    json,
    { flaggedJourneys, nonFlaggedJourneys, seenVignettes, currentState: state },
    true,
    currentDate
  );
  const nextVignetteID = nextVignettes[0];
  const nextVignette = json.vignettes[nextVignetteID!] as VignetteType | undefined;
  let hasQuiz = !!nextVignette?.quiz?.questions.length;
  if (DEMO_PRETEND_ALL_HAVE_QUIZES) {
    // FIXME: Purely for demonstration, pretend we have all the quizs
    hasQuiz = true;
  }
  const shouldShowQuiz =
    (hasQuiz && showQuizOnFirstVideo && !state?.watchedVideosSinceLastQuiz) ||
    (state && state?.watchedVideosSinceLastQuiz >= GAP_BETWEEN_QUIZES) ||
    false;
  return { nextVignetteID, nextVignette, nextNextVignette: json.vignettes[nextVignettes[1]!], shouldShowQuiz, state };
}

export function calculateNextPathwayStepOrThrow(
  json: VignettesJsonFileType,
  user: UserData,
  currentDate: Date,
  showQuizOnFirstVideo: boolean = false
): {
  nextVignetteID: string;
  nextVignette: VignetteType | undefined;
  nextNextVignette: VignetteType | undefined;
  shouldShowQuiz: boolean;
  state: UserState | undefined;
} {
  const ret = calculateNextPathwayStep(json, user, currentDate, showQuizOnFirstVideo);
  if (!ret.nextVignetteID) {
    throw new Error('No more videos to watch');
  }
  return ret as {
    nextVignetteID: string;
    nextVignette: VignetteType | undefined;
    nextNextVignette: VignetteType | undefined;
    shouldShowQuiz: boolean;
    state: UserState | undefined;
  };
}

/**
 * Update the user state to reflect that the user has watched a video.
 */
export function updateStateUserWatchedVideo(
  json: VignettesJsonFileType,
  user: UserData,
  vignetteID: string,
  didQuiz: boolean,
  currentDate: Date
): UserData {
  if (!vignetteID) return user;
  const watchedVideosSinceLastQuiz = didQuiz
    ? 0
    : user.currentState?.watchedVideosSinceLastQuiz
    ? user.currentState.watchedVideosSinceLastQuiz + 1
    : 1;
  const journey = json.vignettes[vignetteID]?.journeys[0]! as JourneyType;
  const lastJourneysWatched = [journey, ...(user.currentState?.lastJourneysWatched || [])];
  const lastJourneysWatchedDate = [currentDate, ...(user.currentState?.lastJourneysWatchedDate || [])];
  return {
    ...user,
    seenVignettes: new Set(user.seenVignettes).add(vignetteID),
    currentState: { ...user.currentState, watchedVideosSinceLastQuiz, lastJourneysWatched, lastJourneysWatchedDate },
  };
}

export function prettyConsoleLogVignetteHeader(
  { flaggedJourneys, nonFlaggedJourneys, seenVignettes }: UserData,
  header?: string
) {
  // Now print out the result in a pretty way using colors
  console.log('\x1b[1m\x1b[32m~'.repeat(86) + '\x1b[0m');
  const None = '\x1b[3mNone\x1b[0m';

  console.log(
    `\n\x1b[1m${
      header ? `${header}: ` : ''
    }\x1b[32mUser's customized Pathway (Vignettes to watch):\x1b[0m\n - \x1b[31mAbnormal (Flagged)\x1b[0m: ${
      flaggedJourneys.join(', ') || None
    }\n - \x1b[32mNormal (Non-flagged)\x1b[0m: ${
      nonFlaggedJourneys.join(', ') || None
    }\n\x1b[1m - Already watched:\x1b[0m ${Array.from(seenVignettes).join(', ') || None}\x1b[0m\n`
  );
  // Fancy wavy line
  console.log('\x1b[1m\x1b[32m~'.repeat(86) + '\x1b[0m');
}

export function prettyConsoleLogVignette(
  json: VignettesJsonFileType,
  { flaggedJourneys, nonFlaggedJourneys }: UserData,
  vignetteId: string,
  showQuiz = false,
  s: UserState | undefined
) {
  const Abnormal = '\x1b[31mAbnormal\x1b[0m';
  const Normal = '\x1b[32mNormal  \x1b[0m';
  const NoData = '\x1b[34mNo Data \x1b[0m';
  const vignette = json.vignettes[vignetteId];
  if (!vignette) {
    throw new Error(`Vignette "${vignetteId}" not found`);
  }
  const journey = vignette.journeys.join(', ');
  const journeyIndex = journeyNameToIndex.get(vignette.journeys[0] as JourneyType);
  // convert index to like a,b,c,
  const journeyCharacter = ['-', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n'][journeyIndex!];
  // console.log(`\x1b[33m${vignette}\x1b[0m`);
  const normalState = flaggedJourneys.includes(journey as JourneyType)
    ? Abnormal
    : nonFlaggedJourneys.includes(journey as PrimaryJourneyType)
    ? Normal
    : NoData;
  if (!OUTPUT_CSV) {
    // Show internal state in light grey
    console.log(
      `\x1b[33m${vignette.id.padEnd(4)} ${vignette.name.padEnd(90)}\x1b[0m ${journey.padEnd(
        20
      )} ${journeyCharacter} ${normalState} ${showQuiz ? 'quiz' : '    '} ${debugVignetteReason}`
    );
  } else {
    const SEP = ';'; // seperator
    if (MINIMAL_OUTPUT) {
      console.log(`${vignette.id}${SEP}${vignette.name}${SEP}${journey}`);
    } else {
      console.log(
        `${vignette.id}${SEP}${vignette.name}${SEP}${journey}${SEP}${journeyCharacter}${SEP}${normalState}${SEP}${
          showQuiz ? 'quiz' : ''
        }${SEP}${debugVignetteReason}`
      );
    }
  }
}

let debugVignetteReason = '';

/**
 * This is the main state machine that determines the next video to show.
 * It takes the current state, and the vignettes to show, and returns the next video to show and a new state.
 * If the user indeed watches the video, update the user state to that new state.
 *
  The way that this works is:

  - For each user, we store:
      The vignettes they have watched, and the journey those videos are in

  - When we want to know what video to show the user next:
     1. Look at the last 4 videos they watched. Does it include the first video of the flagged journey?
        No: We are in the 'TWO' - RETURN the first video of the flagged journey
        Yes: We are in the 'FOUR' - Continue:
     2. Look at the last 2 videos watched. Are they are the same journey, and most recent journey is flagged?
        Yes: RETURN the next video in that journey
        No: Continue:
     3. Look at all the recent vignettes and compare with flagged journeys.  Are there any flagged journies we haven't watched yet?
        Yes: RETURN the first video of that first flagged journey
        No: Continue:
     4. Look at the last journey that they user watched that wasn't the primary flagged journey. Move to the next journey in the list to watch,
        and RETURN the first video of that journey.

    Example:
     1. Example:
        Say the user watched: [ Blood Sugar, Stress, Stress, Activity]
        If Sleep is the primary, because it's not in that list of the last 4 journeys watched, return Sleep.
        If Stress is the primary, because it's in that list, go to step 3
     2. Example:
            User has watched [Sleep, Sleep].   Both are the same journey, so continue to step 2.
            User has watched Blood Sugar then Sleep. They are different. Sleep is most recent.  If Sleep is flagged, then return next Sleep video.
     3. "Recent" means: N := 3 * max(all.length - 1, 2)
        So for example, if a user has one non-essential flagged journey (e.g. smoking) and there are 5 essential journeys,
        then all.length = 5+1 = 6.  So N = 3 * (6-1) = 15
     4. Example:
        Say they watched  [Activity, Stress, Stress]  and Stress is the primary flagged journey.  So Activity is the most recent non-primary-flagged journey.
        So whatever is the next journey, e.g. Sleep.  Return that - e.g. Sleep.
 */
function stateMachineGetNextVignette(
  json: VignettesJsonFileType,
  { flaggedJourneys, nonFlaggedJourneys, seenVignettes, currentState }: UserData,
  showOtherJourneysToo: boolean,
  debug = false
): { v: string | undefined; debug: string } {
  // We follow a 2-4 pattern.
  // First we show two videos from the first flaggedJourney, then we show four videos from the other journeys, and repeat.
  //  These constants look funny, but are just to help with documentating.
  const TWO = 2;
  const FOUR = 4;

  if (!currentState) {
    currentState = {
      watchedVideosSinceLastQuiz: 0,
      lastJourneysWatched: [],
      lastJourneysWatchedDate: [],
    };
  }

  // 1. If there are any vignettes in "Show Immediately", then show those first
  const showImmediately = json.journeys['Show Immediately']?.vignettes.find((v) => !seenVignettes.has(v));
  if (showImmediately) {
    return { v: showImmediately, debug: 'Show Immediately' };
  }

  /// Setup
  /// 2. Filter flaggedJourneys and nonFlaggedJourneys to remove journeys that have no more vignettes to watch
  function getListOfJourneyVignettes() {
    const journeysSet = new Set([...flaggedJourneys, ...nonFlaggedJourneys]);
    // Now recombine
    const journeysToShow = {
      priority: flaggedJourneys,
      lowPriority: showOtherJourneysToo
        ? [...nonFlaggedJourneys, ...primaryJourneys.filter((j) => !journeysSet.has(j))]
        : undefined,
    };
    function journiesToVignettes(journies: JourneyType[], howManyToShowTogether: number): JourneyVignetteInfo[] {
      return journies
        .map((journey) => ({
          journey,
          journeyN: journeyToJourneyN(journey),
          vignette: json.journeys[journey]?.vignettes.find((v) => !seenVignettes.has(v)) as string,
          howManyToShowTogether,
        }))
        .filter((v) => v.vignette);
    }
    // Get all vignettes relevant
    const [highestPriorityJourney, ...priority] = journiesToVignettes(journeysToShow.priority, 2); // High priority ones, group in 2s
    const lowPriority = journiesToVignettes(journeysToShow.lowPriority || [], 1); // Low priority ones, group in 1s
    const all = [...priority, ...lowPriority];
    return {
      highestPriorityJourney,
      priority,
      all,
    };
  }
  // 3. Define:
  //   highestPriorityJourney - First item in flaggedJourneys
  //   priority - Remaining items in flaggedJourneys
  //   all - All items in flaggedJourneys and nonFlaggedJourneys, but not including highestPriorityJourney
  const { highestPriorityJourney, priority, all } = getListOfJourneyVignettes();

  // 4. If 'all' is empty, then there are no more vignettes to watch so stop
  if (all.length === 0) {
    if (highestPriorityJourney) {
      return { v: highestPriorityJourney.vignette, debug: 'Only one journey left' };
    }
    return { v: undefined, debug: 'No more videos' };
  }
  const allJourneySet = new Set(all.map((x) => x.journey));
  // Otherwise, data_algorithm:
  const lastJourneyWatched = currentState.lastJourneysWatched[0];
  const justWatchedSameJourneyTwice = lastJourneyWatched && lastJourneyWatched === currentState.lastJourneysWatched[1];
  // 1. Count the number of times highestPriorityJourney appears in the first (most recent) 4 entries of lastJourneysWatched.
  //    If 0, then stop and return highestPriorityJourney as the next journey to watch
  //    If 1 or 2 and the last journey watched was highPriorityJourney but the journey before that was not, then stop and
  //    return highestPriorityJourney as the next journey to watch
  if (highestPriorityJourney) {
    const potentialNextJourney = highestPriorityJourney.journey;
    const count = currentState.lastJourneysWatched
      .slice(0, FOUR)
      .reduce((acc, j) => acc + (j === potentialNextJourney ? 1 : 0), 0);
    if (count === 0) {
      return { v: highestPriorityJourney.vignette, debug: 'TWO' };
    }
    if (count <= TWO && lastJourneyWatched === potentialNextJourney && !justWatchedSameJourneyTwice) {
      return {
        v: highestPriorityJourney.vignette,
        debug: `TWO${count < TWO ? '' : ' (forcing to get back on track)'}`,
      };
    }
  }

  // 2. If our most recent journey is in `priority` but the second most recent journey is not, stop and return that as the next journey to watch
  if (!justWatchedSameJourneyTwice) {
    const journey = priority.find((x) => x.journey === lastJourneyWatched);
    if (journey) {
      return { v: journey.vignette, debug: 'Watch the same high-priority journey again' };
    }
  }

  // 3. To better handle when the persona changes:
  //    For each journey in flaggedJourneys:
  //    (Note this uses an upper bound because this logic is not that important - it's a way to
  //    bump priority for the flagged journeys when the user's flagged journeys has changed and we haven't watched that priority
  //    in a while.  If the user hasn't changed their flagged journeys, step 4 will be called)
  let best: { v: string; index: number; debug: string } | undefined = undefined;
  for (const journey of priority) {
    // 3a. N := 3 * max(all.length - 1, 2)  // Note this is an upper bound
    const N = 3 * Math.max(all.length - 1, 2);
    // 3b. M := if journey is primary then 2 else 1
    const M = journey.howManyToShowTogether;
    // 3c. count number of times journey appears in the first N entries of lastJourneysWatched.
    // if 0 than M, then stop and return journey as the next journey to watch.
    // if less than M, then mark this as the best journey to watch next
    // Note: We only include journeys that are in the all list
    const count = currentState.lastJourneysWatched
      .filter((j) => allJourneySet.has(j))
      .slice(0, N)
      .reduce((acc, j) => acc + (j === journey.journey ? 1 : 0), 0);
    if (count === 0) {
      return { v: journey.vignette, debug: `FOUR - Priority bump! ${N}` };
    }
    if (count < M) {
      const index = currentState.lastJourneysWatched.indexOf(journey.journey);
      if (!best || index > best.index) {
        best = { v: journey.vignette, index, debug: 'FOUR - Priority bump!' };
      }
    }
  }
  if (best) {
    return { v: best.v, debug: best.debug };
  }

  // 4. We have a list of all the journeys to watch in the order of priority.
  // Find where we are in this list by:
  // - Get the first journey in  lastJourneysWatched that isn't the highest priority journey.
  // - Find where this is in the all list.
  // - Return the next journey in that all list, skipping over highestPriorityJourney if possible.
  const previousFourJourney = currentState.lastJourneysWatched.find((j) => j !== highestPriorityJourney?.journey);
  // The get the index of this journey in the list of all journeys
  let index = previousFourJourney ? (all.findIndex((j) => j.journey === previousFourJourney) + 1) % all.length : 0;
  // Skip over the "TWO" - the highest priority journey
  return {
    v: all[index]?.vignette,
    debug: `FOUR - ${index} ${previousFourJourney ? `incrementing from ${previousFourJourney}` : ''}`,
  };
}
